import React, { useEffect, useRef, useState } from 'react';
import styles from './home.module.scss';
import SceneComponent, { Scene as SceneType } from './component/sceneContainer/scene';
import cn from 'clsx';
import { fullTemplate, initialData, urlConfigs } from './data';
import Outline from './component/outlineContainer/outline';
import Modal from 'react-modal';
import {Helmet} from "react-helmet";

export enum OutlineView {
  ReadOutlineView,
  EditorView
}

function App() {
  const localStorageKey = 'and-then-outline';
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState<boolean>(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState<boolean>(false);
  const [sceneOutline, setSceneOutline] = useState<Array<SceneType>>(fullTemplate);
  const [selectedView, setSelectedView] = useState<OutlineView>(OutlineView.EditorView);
  const sceneRefs = useRef<{ [key: number]: React.RefObject<HTMLDivElement> }>({});

  // Load scene outline from localStorage on initial render
  useEffect(() => {
    const savedOutline = localStorage.getItem(localStorageKey);
    if (savedOutline) {
      setSceneOutline(JSON.parse(savedOutline));
    }
  }, []);

  const clearOutline = () => {
    exportOutlineContent();
    localStorage.removeItem(localStorageKey);
    setSceneOutline(initialData);
    setShowConfirmDeleteModal(false);
  }

  const onOutlineChange = (updatedScene: SceneType) => {
    const updatedOutline = sceneOutline.map((scene) =>
      scene.sceneNumber === updatedScene.sceneNumber ? updatedScene : scene
    );
    setSceneOutline(updatedOutline);
    localStorage.setItem(localStorageKey, JSON.stringify(updatedOutline));
  };

  const handleAddScene = () => {
    const sceneNumbers = sceneOutline.map(scene => scene.sceneNumber);
    const maxSceneNumber = Math.max(0, ...sceneNumbers);  // Default to 0 if no scenes

    // Get an array of IDs, convert them to integers, and then find the maximum
    const sceneIds = sceneOutline.map(scene => scene.id);
    const maxId = Math.max(0, ...sceneIds);  // Default to 0 if no IDs

    const newScene = {
      id: maxId + 1,
      scene: "",
      sceneNumber: maxSceneNumber + 1,
      chapter: 1,
      setting: "",
      sceneReasoning: "",
      needsEditing: true,
    };

    const newOutline = [...sceneOutline, newScene];
    setSceneOutline(newOutline);
    localStorage.setItem(localStorageKey, JSON.stringify(newOutline));
  }

  const handleDeleteScene = (sceneToDelete: SceneType) => {
    const updatedOutline = sceneOutline.filter((scene) => {
      return scene.id !== sceneToDelete.id
    });
    setSceneOutline(updatedOutline);
    localStorage.setItem(localStorageKey, JSON.stringify(updatedOutline));
  }

  const scrollToScene = (scene: SceneType) => {
    console.log(sceneRefs);

    if (sceneRefs.current[scene.id]) {

      sceneRefs?.current[scene.id]?.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const toggleViewChange = () => {
    if (selectedView === OutlineView.EditorView) {
      setSelectedView(OutlineView.ReadOutlineView)
    } else {
      setSelectedView(OutlineView.EditorView)
    }
  }

  const exportOutlineContent = () => {
    const content = sceneOutline.map(scene => {
      return `Chapter ${scene.chapter}, Scene ${scene.sceneNumber}\n` +
        `${scene.scene}\n` +
        `${scene.setting}\n` +
        `${scene.sceneDetails}\n`;
    }).join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'scenes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
    <Helmet>
      <meta charSet="utf-8" />
      <title>And Then? | Simple Outline Tool</title>
    </Helmet>
    <div className={styles.homePage}>
      <div className={styles.leftSidebar}>
        <div className={styles.leftSidebarMeta}>
          <h1><i></i>And then?</h1>
          <p>The <i>And Then Editor</i> is a simple tool writers can use to plan out their scenes.</p>
          <button onClick={() => {
            setShowInstructionsModal(true)
          }}>How to use?</button>
          <div className={styles.legendContainer}>
            <p className={styles.fontBold}>Legend:</p>
            <p className={styles.rowCenter}>
              <div className={styles.warning}><span>!</span></div><span className={styles.fontHighlight}>
              Needs editing</span>
            </p>
          </div>

        </div>
        <div className={styles.outline}>
          <h2>Outline Navigator</h2>
          <div className={styles.outlineNavBox}>
            {sceneOutline.map((scene) => (
              <div onClick={() => {
                scrollToScene(scene);
              }} key={scene.id}>
                Chapter {scene.chapter}, Scene {scene.sceneNumber}
              </div>
            ))}
          </div>
          <button onClick={() => {
            setShowConfirmDeleteModal(true)
          }}>Clear my outline</button>
        </div>
        <div className={styles.acknowledgements}>
          <p><i>And Then Editor v1</i></p>
          <p><a href={urlConfigs.website}  rel="noreferrer"  target='_blank'>Made with love by Nichie 💖</a></p>
          <p><a href={urlConfigs.newsletter}  rel="noreferrer" target='_blank'>Subscribe to my newsletter</a></p>
          <p><a href={urlConfigs.kofi}  rel="noreferrer" target='_blank'>Buy me a coffee?</a></p>

        </div>

      </div>
      <div className={styles.rightSidebar}>
        {sceneOutline?.map((scene, index) => {

          // Initialize ref for each scene
          if (!sceneRefs.current[scene.id]) {
            sceneRefs.current[scene.id] = React.createRef<HTMLDivElement>();
          }

          return (
            <div ref={sceneRefs.current[scene.id]} key={scene.id}>
              {selectedView === OutlineView.EditorView &&
                <SceneComponent key={`${index}-${scene.id}`}
                  handleDeleteScene={handleDeleteScene}
                  scene={scene}
                  onSceneChange={onOutlineChange} />
              }
              {selectedView === OutlineView.ReadOutlineView &&
                <Outline key={`${index}-${scene.id}`}
                  scene={scene} />
              }
            </div>)
        })}
      </div>

      <div className={styles.featureButtonContainer}>
        <div>
          <button onClick={exportOutlineContent} className={cn(styles.button)}>Export outline</button>
        </div>
        <div>
          <button onClick={toggleViewChange} className={cn(styles.button)}>{selectedView === OutlineView.EditorView ? 'View' : 'Edit'
          } outline</button>
        </div>
        <div>
          <button onClick={handleAddScene} className={cn(styles.button)}>+ Add scene</button>
        </div>
      </div>

      <Modal isOpen={showConfirmDeleteModal} 
      overlayClassName={styles.modalOverlay} className={cn(styles.modal, styles.deleteModal)}>
        <div>
          <h3>Are you sure you want to clear your outline data?</h3>
          <p>I can't bring your outline back if you decide to do this. I'll download a text file for you before I delete this forever.</p>
          <div className={styles.modalButtons}>
            <button onClick={() => {
              setShowConfirmDeleteModal(false)
            }}>No, I changed my mind.</button>
            <button className={styles.negativeButton} onClick={clearOutline}>Yes, delete everything.</button>
          </div>
        </div>
      </Modal>
      <Modal isOpen={showInstructionsModal} 
        overlayClassName={styles.modalOverlay}className={cn(styles.modal, styles.instructionsModal)}>
        <div>
          <div className={styles.rowSpaceBetween}>
            <h3>How do I use this?</h3>
            <button onClick={() => {
              setShowInstructionsModal(false)
            }}>Close instructions</button>
          </div>
          <p>The <i>And Then Editor</i> is a simple visual editor for outlining your scenes for your stories. Your scenes are saved to your local storage. You can navigate through your outline using the Outline Navigator.</p>
          <ol>
            <li>
            Add scenes using the <i>+ Add scene</i> button on the bottom right.
            </li>
            <li>
            Write out your scene, setting, details, and reasoning (including which chapter this scene is for, and if this still needs editing!). If you want to delete a scene, expand it and click the <i>Delete</i> button.
            </li>
            <li>
            Once you're done, view your outline using the <i>View outline</i> button.
            </li>
            <li>
            Lastly, once you're satisfied with how you've outlined your scenes, export your outline. It will download a .txt file for you to use wherever you need it.
            </li>
            </ol>
            <p>When you want to use the tool again to create a new outline, simply click the <i>Clear my entire outline</i> button.</p>
        </div>
      </Modal>
    </div>
    </>

  );
}

export default App;
